import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { journeyAPI } from '../services/api';
import { isJourneyOverdue, isJourneyUpcoming } from '../utils/time';

const Dashboard = ({ journeys, onJourneyCompleted, onJourneyCancelled }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatusData();
  }, [journeys]);

  const loadStatusData = async () => {
    try {
      const response = await journeyAPI.getStatus();
      setStatus(response.data.status);
    } catch (error) {
      console.error('Failed to load status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActiveJourneys = () => {
    return journeys.filter(journey => journey.status === 'active');
  };

  const getUpcomingJourneys = () => {
    return getActiveJourneys().filter(journey =>
      isJourneyUpcoming(journey.return_date, journey.return_time)
    );
  };

  const getOverdueJourneys = () => {
    return getActiveJourneys().filter(journey =>
      isJourneyOverdue(journey.return_date, journey.return_time)
    );
  };

  const formatDateTime = (date, time) => {
    try {
      const dateTime = parseISO(`${date}T${time}:00`);
      return format(dateTime, 'MMM dd, yyyy \'at\' HH:mm');
    } catch (error) {
      return `${date} at ${time}`;
    }
  };

  const upcomingJourneys = getUpcomingJourneys();
  const overdueJourneys = getOverdueJourneys();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Hiking Safety Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Monitor your remote hiking trips and emergency notifications
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-xl">📊</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {getActiveJourneys().length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-green-600 text-xl">⏰</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">
                {upcomingJourneys.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <span className="text-yellow-600 text-xl">⚠️</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-gray-900">
                {overdueJourneys.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <span className="text-red-600 text-xl">🚨</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Notified</p>
              <p className="text-2xl font-bold text-gray-900">
                {status?.overdue_notified || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/create"
            className="flex items-center justify-center p-4 bg-green-50 hover:bg-green-100 rounded-lg border-2 border-dashed border-green-200 hover:border-green-300 transition-colors"
          >
            <div className="text-center">
              <span className="text-2xl mb-2 block">🥾</span>
              <span className="text-green-700 font-medium">Register New Journey</span>
            </div>
          </Link>
          
          <Link
            to="/journeys"
            className="flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-dashed border-blue-200 hover:border-blue-300 transition-colors"
          >
            <div className="text-center">
              <span className="text-2xl mb-2 block">📋</span>
              <span className="text-blue-700 font-medium">View All Journeys</span>
            </div>
          </Link>
          
          <Link
            to="/settings"
            className="flex items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border-2 border-dashed border-purple-200 hover:border-purple-300 transition-colors"
          >
            <div className="text-center">
              <span className="text-2xl mb-2 block">⚙️</span>
              <span className="text-purple-700 font-medium">Settings & Testing</span>
            </div>
          </Link>
        </div>
      </div>

      {/* Current Journeys */}
      {(upcomingJourneys.length > 0 || overdueJourneys.length > 0) && (
        <div className="space-y-6">
          {/* Overdue Journeys - High Priority */}
          {overdueJourneys.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                <span className="mr-2">🚨</span>
                Overdue Journeys - Action Required
              </h2>
              <div className="space-y-3">
                {overdueJourneys.map(journey => (
                  <div key={journey.id} className="bg-white p-4 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{journey.destination}</h3>
                        <p className="text-sm text-red-600">
                          Expected return: {formatDateTime(journey.return_date, journey.return_time)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Emergency contact: {journey.contact_name}
                        </p>
                      </div>
                      <button
                        onClick={() => onJourneyCompleted(journey.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                      >
                        ✅ I'm Safe!
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Journeys */}
          {upcomingJourneys.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Active Upcoming Journeys
              </h2>
              <div className="space-y-3">
                {upcomingJourneys.slice(0, 3).map(journey => (
                  <div key={journey.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{journey.destination}</h3>
                        <p className="text-sm text-gray-600">
                          Expected return: {formatDateTime(journey.return_date, journey.return_time)}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onJourneyCompleted(journey.id)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Complete
                        </button>
                        <button
                          onClick={() => onJourneyCancelled(journey.id)}
                          className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {upcomingJourneys.length > 3 && (
                  <Link
                    to="/journeys"
                    className="block text-center py-2 text-green-600 hover:text-green-700 font-medium"
                  >
                    View {upcomingJourneys.length - 3} more upcoming journeys →
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {getActiveJourneys().length === 0 && (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🏔️</span>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No active journeys</h3>
          <p className="text-gray-600 mb-6">
            Register your next hiking trip to enable safety monitoring
          </p>
          <Link
            to="/create"
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <span className="mr-2">🥾</span>
            Register Your Next Journey
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
